import { Idea } from '../types';

// API endpoint for AI analysis (calls our Vercel function)
const AI_ANALYSIS_ENDPOINT = '/api/ai-analysis';

export interface AIAnalysisResult {
  marketOpportunity: string;
  technicalComplexity: string;
  businessValue: string;
  recommendedSteps: string[];
  risks: string[];
  timeline: string;
  confidence: number;
}

// Filter out LLM procedural messaging and return only the substantive analysis content
const filterAnalysisContent = (text: string): string => {
  if (!text) return '';
  
  // Common patterns to remove
  const patternsToRemove = [
    /I'll conduct a comprehensive.*?(?=\n\n|$)/gi,
    /Let me start by.*?(?=\n\n|$)/gi,
    /Now let me search for.*?(?=\n\n|$)/gi,
    /Let me search for.*?(?=\n\n|$)/gi,
    /I'll search for.*?(?=\n\n|$)/gi,
    /Based on my comprehensive research, I'll now provide.*?(?=\n\n|$)/gi,
    /I'll analyze.*?(?=\n\n|$)/gi,
    /Let me research.*?(?=\n\n|$)/gi,
    /I'll research.*?(?=\n\n|$)/gi,
    /I need to.*?(?=\n\n|$)/gi,
    /Let me gather.*?(?=\n\n|$)/gi,
    /I'll gather.*?(?=\n\n|$)/gi,
    /First, let me.*?(?=\n\n|$)/gi,
    /Next, I'll.*?(?=\n\n|$)/gi,
    /Finally, let me.*?(?=\n\n|$)/gi,
  ];
  
  let filteredText = text;
  
  // Remove each pattern
  patternsToRemove.forEach(pattern => {
    filteredText = filteredText.replace(pattern, '');
  });
  
  // Clean up extra whitespace and line breaks
  filteredText = filteredText
    .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with 2
    .replace(/^\s+/gm, '') // Remove leading whitespace from lines
    .trim(); // Remove leading/trailing whitespace
  
  // If the filtered text is too short, return the original (safety check)
  if (filteredText.length < 100) {
    return text;
  }
  
  return filteredText;
};

// Category-specific prompt generation for Standard AI Analysis (no web search)
const getStandardAnalysisPrompt = (idea: Idea): string => {
  const commonContext = `
**COMPANY CONTEXT - KAJEET:**
Kajeet (kajeet.com) is a leading provider of IoT connectivity and device management solutions serving enterprise, education, government, and transportation markets. Founded in 2003, Kajeet specializes in secure cellular connectivity with comprehensive device management platforms.

**KAJEET'S CORE BUSINESS:**
• **IoT Connectivity Services**: Secure cellular connectivity for IoT devices across multiple carriers (Verizon, AT&T, T-Mobile) with support for mobile network switching via eSIM
• **Device Management Platform**: Sentinel Cloud-based portal for device lifecycle management, usage monitoring, security, and application control
• **Vertical Market Focus**: Education (student devices, school bus connectivity), Government (first responders, military), Transportation (fleet management), Enterprise IoT
• **Key Differentiators**: Security-first approach, carrier-agnostic solutions, comprehensive device management, regulatory compliance

**KAJEET'S CUSTOMERS & MARKETS:**
• **Education Sector**: K-12 schools, buses, higher education, libraries - providing secure internet access for student devices and digital equity programs like e-rate
• **Government**: Federal agencies, state/local government, first responders, military - secure connectivity for critical communications
• **Transportation**: Fleet management, asset tracking, connected vehicle solutions
• **Enterprise**: IoT deployments for retail, healthcare, manufacturing, and smart city applications
• **Customer Profile**: Typically enterprise accounts with 100+ devices, high security requirements, and compliance needs

**KAJEET'S COMPETITIVE POSITION:**
• **Primary Competitors**: Verizon Connect, AT&T IoT, T-Mobile Business, Hologram, Particle, Twilio
• **Competitive Advantages**: Carrier-agnostic flexibility, education market expertise, government security clearances, white-glove service
• **Market Challenges**: Larger carriers with direct relationships, price competition, technology complexity

**KAJEET'S MARKET POSITION:**
• **Market Share**: 10% of the IoT connectivity market
• **Market Position**: Leader in education, government, and transportation markets.  Kajeet would really like to provide more tools to developers and IoT Solutions Providers - making it easier to build end-to-end solutions.
• **Market Challenges**: Price competition, technology complexity, regulatory compliance

**KAJEET'S TECHNOLOGY STACK:**
• **Connectivity**: Multi-carrier cellular (4G LTE, 5G), private networks, hybrid connectivity
• **Management Platform**: Cloud-based SaaS platform, APIs, mobile applications
• **Security**: Enterprise-grade security, compliance certifications, content filtering
• **Integration**: CRM/ERP integrations, third-party platform APIs, custom development capabilities

**KAJEET'S BUSINESS MODEL:**
• **Revenue Streams**: Monthly connectivity fees, device management platform fees, professional services, hardware sales
• **Pricing Model**: Tiered pricing based on data usage, device count, and service level
• **Customer Lifecycle**: Typically long-term contracts (1-3 years), high customer retention, expansion opportunities

**IDEA TO ANALYZE:**
- **Title:** ${idea.title}
- **Description:** ${idea.description}
- **Category:** ${idea.category}
- **Current Status:** ${idea.status}
- **Internal Effort Score (1-10):** ${idea.effort}
- **Internal Value Score (1-10):** ${idea.value}
- **Priority Score:** ${idea.score?.toFixed(2) || 'N/A'}
- **Tags:** ${idea.tags.join(', ') || 'None'}

**ANALYSIS INSTRUCTIONS:**
Analyze this idea specifically in the context of Kajeet's business model, customer base, competitive position, and technology capabilities. Consider how this idea aligns with Kajeet's core strengths in secure IoT connectivity and device management. Reference Kajeet's specific markets (education, government, transportation, enterprise) and competitive advantages throughout your analysis.
`;

  switch (idea.category) {
    case 'Process':
      return `
You are a senior business process consultant and operations expert specializing in telecommunications and IoT companies. You are analyzing a business process improvement idea for Kajeet, a leading IoT connectivity provider serving education, government, transportation, and enterprise markets.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Analyze the specific idea "${idea.title}" and how it would improve Kajeet's business operations. Focus on this exact process improvement idea and its impact on Kajeet's IoT connectivity and device management business.

## 1. **CURRENT STATE & PROCESS ANALYSIS**
- **Process Being Improved**: Analyze exactly what process this idea aims to improve and how it relates to Kajeet's core operations (connectivity management, device lifecycle, customer support, billing)
- **Current State Assessment**: Evaluate the current state of the specific process this idea targets within Kajeet's operations - if not directly known, assume common current states at businesses like Kajeet
- **Customer Impact**: Assess how implementing this idea would specifically benefit Kajeet's education, government, transportation, and enterprise customers

## 2. **SOLUTION & IMPLEMENTATION ANALYSIS**
- **How This Idea Works**: Analyze the specific approach described in the idea and how it would function within Kajeet's environment
- **Technology Requirements**: Evaluate what specific tools, platforms, and automation solutions would be needed to implement this idea
- **Implementation Approach**: Determine the best approach for implementing this specific idea based on its requirements and Kajeet's capabilities
- **Expected Outcomes**: Analyze the specific ROI and time savings this idea would deliver for Kajeet

## 3. **BUSINESS BENEFITS & IMPACT**
- **Operational Efficiency**: Quantify the specific time savings, resource optimization, and error reduction this idea would deliver for Kajeet
- **Customer Experience**: Evaluate how implementing this idea would specifically improve Kajeet's customer satisfaction and service delivery
- **Employee Impact**: Assess how this idea would improve staff efficiency and job satisfaction at Kajeet
- **Cost Savings**: Estimate the specific direct and indirect cost reductions this idea would generate for Kajeet
- **Revenue Impact**: Analyze how this idea would specifically drive revenue growth for Kajeet's IoT connectivity business

## 4. **IMPLEMENTATION STRATEGY**
- **Implementation Plan**: Recommend the specific steps needed to implement this idea at Kajeet
- **Success Metrics**: Define specific KPIs and success metrics for measuring the impact of this idea
- **Change Management**: Develop change management strategies specifically for implementing this idea at Kajeet

## 5. **RISKS & MITIGATION**
- **Implementation Challenges**: Identify specific challenges and pitfalls that could arise when implementing this idea at Kajeet
- **Risk Mitigation**: Recommend specific strategies for managing risks and ensuring successful implementation
- **Quality Assurance**: Define quality control approaches specifically for this idea

## 6. **COMPETITIVE & STRATEGIC IMPACT**
- **Competitive Advantage**: Analyze how implementing this idea would strengthen Kajeet's competitive position
- **Market Impact**: Evaluate how this idea aligns with market trends in IoT connectivity and device management
- **Regulatory Compliance**: Consider any regulatory or compliance implications of implementing this idea

Focus your entire analysis on the specific idea "${idea.title}" and how it would benefit Kajeet's IoT connectivity business. Reference the idea description: "${idea.description}"
`;

    case 'Technology':
      return `
You are a senior technology architect and platform evaluation expert with deep experience in telecommunications, IoT infrastructure, and enterprise technology implementations. You are analyzing a technology platform or infrastructure idea for Kajeet's IoT connectivity and device management business.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Analyze the specific technology idea "${idea.title}" and evaluate how it would integrate with and enhance Kajeet's IoT connectivity and device management platform. Focus on this exact technology solution and its impact on Kajeet's business.

## 1. **TECHNOLOGY SOLUTION ANALYSIS**
- **Technology Overview**: Analyze the specific technology described in this idea and how it would work within Kajeet's environment
- **Kajeet Integration**: Evaluate how this technology would integrate with Kajeet's existing technology stack (multi-carrier connectivity, Sentinel platform, security infrastructure)
- **Technical Requirements**: Assess the specific technical requirements needed to implement this technology at Kajeet
- **Technology Fit**: Determine how well this technology aligns with Kajeet's current technology architecture and future roadmap

## 2. **TECHNICAL FEASIBILITY**
- **Implementation Complexity**: Evaluate the specific complexity of implementing this technology within Kajeet's existing technology environment
- **Infrastructure Needs**: Assess what specific infrastructure changes or additions would be needed
- **Integration Challenges**: Analyze specific integration challenges with Kajeet's current systems
- **Security & Compliance**: Evaluate how this technology would meet Kajeet's security standards and compliance requirements

## 3. **BUSINESS IMPACT & VALUE**
- **Operational Benefits**: Analyze how this technology would specifically improve Kajeet's operational workflows and system performance
- **Customer Benefits**: Evaluate how this technology would enhance service delivery for Kajeet's education, government, transportation, and enterprise customers
- **Competitive Advantage**: Assess how this technology would strengthen Kajeet's competitive position against Verizon Connect, AT&T IoT, and other competitors
- **Revenue Opportunities**: Analyze specific revenue implications and business model enhancements from implementing this technology

## 4. **STRATEGIC ALIGNMENT**
- **Technology Roadmap**: Evaluate how this technology aligns with Kajeet's technology evolution and future plans
- **Market Position**: Analyze how implementing this technology would support Kajeet's competitive positioning in IoT connectivity
- **Investment Priorities**: Assess whether this technology represents a sound investment relative to its expected business benefits for Kajeet
- **Strategic Risks**: Identify specific risks and mitigation strategies for implementing this technology

## 5. **IMPLEMENTATION PLANNING**
- **Implementation Strategy**: Recommend specific implementation approach for this technology at Kajeet


Focus your entire analysis on the specific technology idea "${idea.title}" and how it would enhance Kajeet's IoT connectivity and device management capabilities. Reference the idea description: "${idea.description}"
`;

    case 'Sales and Marketing':
      return `
You are a senior revenue operations and marketing strategy expert with extensive experience in B2B telecommunications, IoT services, and enterprise technology sales. You are analyzing a sales and marketing idea for Kajeet's IoT connectivity and device management business serving education, government, transportation, and enterprise markets.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Analyze the specific sales and marketing idea "${idea.title}" and evaluate how it would enhance Kajeet's lead generation, revenue growth, and sales effectiveness. Focus on this exact initiative and its impact on Kajeet's IoT connectivity business.

## 1. **MARKET IMPACT ANALYSIS**
- **Opportunity Assessment**: Analyze how this idea would specifically address market opportunities in Kajeet's education, government, transportation, and enterprise sectors
- **Target Segment Impact**: Evaluate how this idea would enhance Kajeet's position within its core markets and customer segments
- **Competitive Advantage**: Assess how this idea would strengthen Kajeet's competitive position against Verizon Connect, AT&T IoT, T-Mobile Business, and other competitors
- **Market Alignment**: Determine how this idea aligns with current market trends and customer needs in IoT connectivity

## 2. **LEAD GENERATION & ACQUISITION IMPACT**
- **Lead Generation Enhancement**: Evaluate how this idea would specifically improve lead generation for Kajeet's IoT connectivity services
- **Customer Acquisition**: Analyze how this idea would enhance customer acquisition approaches for Kajeet's target markets
- **Conversion Improvement**: Assess how this idea would improve conversion rates and sales funnel performance for Kajeet
- **Digital Marketing Impact**: Determine how this idea would enhance digital marketing effectiveness for Kajeet's technical B2B services

## 3. **REVENUE IMPACT & GROWTH**
- **Sales Cycle Impact**: Evaluate how this idea would specifically impact Kajeet's sales cycle length and deal closure rates
- **Deal Size Enhancement**: Analyze how this idea would increase average contract values and account expansion for Kajeet
- **Customer Lifetime Value**: Assess how this idea would impact customer retention and lifetime value for Kajeet
- **Revenue Growth**: Determine the percentage revenue growth potential from implementing this idea

## 4. **SALES EFFECTIVENESS IMPACT**
- **Sales Team Enhancement**: Evaluate how this idea would improve Kajeet's sales team efficiency and performance
- **Sales Enablement**: Assess how this idea would enhance sales tools, processes, and content for Kajeet's team
- **Territory Optimization**: Analyze how this idea would improve territory coverage and account management for Kajeet
- **Performance Metrics**: Define specific KPIs for measuring the success of this idea implementation

## 5. **MARKETING STRATEGY IMPACT**
- **Brand Enhancement**: Evaluate how this idea would impact Kajeet's brand positioning and market perception
- **Content Strategy**: Assess how this idea would enhance content marketing for educating and engaging Kajeet's target audiences
- **Campaign Effectiveness**: Analyze how this idea would improve marketing campaign performance for Kajeet
- **Customer Communication**: Determine how this idea would enhance customer communication and engagement strategies

## 6. **IMPLEMENTATION STRATEGY**
- **Resource Requirements**: Estimate the general personnel and technology requirements for implementing this idea
- **Success Measurement**: Define metrics for measuring the success of this idea implementation

## 7. **STRATEGIC OPPORTUNITIES**
- **Market Expansion**: Analyze how this idea could help Kajeet expand into new market segments or geographies
- **Partnership Opportunities**: Evaluate how this idea could create strategic partnership opportunities for Kajeet
- **Customer Success**: Assess how this idea would enhance customer success strategies that support Kajeet's sales and marketing
- **Long-term Impact**: Consider the long-term strategic implications and growth opportunities from this idea

Focus your entire analysis on the specific sales and marketing idea "${idea.title}" and how it would enhance Kajeet's revenue generation and market position. Reference the idea description: "${idea.description}"
`;

    case 'Feature':
    case 'Product':
    case 'Innovation':
    default:
      return `
You are a senior product strategist and technology advisor with deep expertise in IoT, telecommunications, and enterprise technology solutions. You are analyzing a product idea for Kajeet's IoT connectivity and device management platform serving education, government, transportation, and enterprise markets.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Analyze the specific product idea "${idea.title}" and evaluate its strategic value for Kajeet's IoT connectivity and device management platform. Focus on this exact product concept and its potential impact on Kajeet's business and customers.

## 1. **MARKET OPPORTUNITY ANALYSIS**
- **Product-Market Fit**: Evaluate how this product aligns with market needs in Kajeet's education, government, transportation, and enterprise markets. Also how it would fit with Kajeet's desire to court more developers and IoT Solutions Providers and make it easier to build end-to-end IoT solutions
- **Competitive Positioning**: Analyze how this product would position Kajeet against competitors like Verizon Connect, AT&T IoT, and T-Mobile Business
- **Customer Demand**: Assess specific customer demand for this product within Kajeet's target markets
- **Market Timing**: Consider the market readiness and optimal timing for introducing this product

## 2. **CUSTOMER SEGMENT ANALYSIS**
- **Target Alignment**: Evaluate how this product aligns with Kajeet's core customer segments (K-12 education, government agencies, fleet management, enterprise IoT)
- **Customer Journey**: Analyze how this product fits into Kajeet customers' decision-making and procurement processes
- **Value Proposition**: Define the unique value propositions that this product would offer to Kajeet's target customers
- **Adoption Patterns**: Assess likely adoption patterns and implementation challenges for this product

## 3. **TECHNICAL FEASIBILITY**
- **Integration Requirements**: Evaluate how this product would integrate with Kajeet's existing IoT connectivity platform and Sentinel device management infrastructure
- **Development Complexity**: Assess the specific development requirements and technical challenges for building this product
- **Security & Compliance**: Evaluate how this product would meet security and compliance requirements for Kajeet's regulated markets

## 4. **BUSINESS MODEL ANALYSIS**
- **Revenue Model**: List possible pricing strategies for this product within Kajeet's business
- **Cost Structure**: Describe general development, operational, and go-to-market investments needed for this product

## 5. **STRATEGIC ALIGNMENT**
- **Product Portfolio Fit**: Analyze how this product fits within Kajeet's overall product portfolio and strategy
- **Competitive Differentiation**: Evaluate how this product would create sustainable competitive advantages for Kajeet
- **Market Position Impact**: Assess how this product would impact Kajeet's overall market positioning and brand
- **Growth Contribution**: Consider how this product would contribute to Kajeet's growth objectives and strategic goals

## 6. **IMPLEMENTATION STRATEGY**
- **Product Development Plan**: Recommend the specific product development approach and timeline for this product
- **Go-to-Market Strategy**: Outline the go-to-market strategy and launch planning specifically for this product
- **Resource Planning**: Estimate the development, marketing, and operational resource requirements for this product
- **Risk Management**: Identify key risks and mitigation strategies specific to this product

## 7. **SUCCESS METRICS & VALIDATION**
- **Product Success Metrics**: Define specific KPIs for measuring the success of this product
- **Market Validation**: Recommend validation approaches and success criteria for this product

## 8. **STRATEGIC RECOMMENDATIONS**
- **Development Roadmap**: Provide specific recommendations for developing and evolving this product
- **Market Entry Strategy**: Recommend market entry and expansion strategies for this product
- **Partnership Opportunities**: Consider strategic partnerships that could accelerate success of this product
- **Long-term Vision**: Analyze long-term strategic implications and growth potential of this product

Focus your entire analysis on the specific product idea "${idea.title}" and how it would enhance Kajeet's IoT connectivity and device management offerings. Reference the idea description: "${idea.description}"
`;
  }
};

// Category-specific prompt generation for Deep Research (with web search)
const getCategorySpecificPrompt = (idea: Idea): string => {
  const commonContext = `
**COMPANY CONTEXT - KAJEET:**
Kajeet (kajeet.com) is a leading provider of IoT connectivity and device management solutions serving enterprise, education, government, and transportation markets. Founded in 2003, Kajeet specializes in secure cellular connectivity with comprehensive device management platforms.

**KAJEET'S CORE BUSINESS:**
• **IoT Connectivity Services**: Secure cellular connectivity for IoT devices across multiple carriers (Verizon, AT&T, T-Mobile) with support for mobile network switching via eSIM
• **Device Management Platform**: Sentinel Cloud-based portal for device lifecycle management, usage monitoring, security, and application control
• **Vertical Market Focus**: Education (student devices, school bus connectivity), Government (first responders, military), Transportation (fleet management), Enterprise IoT
• **Key Differentiators**: Security-first approach, carrier-agnostic solutions, comprehensive device management, regulatory compliance

**KAJEET'S CUSTOMERS & MARKETS:**
• **Education Sector**: K-12 schools, buses, higher education, libraries - providing secure internet access for student devices and digital equity programs like e-rate
• **Government**: Federal agencies, state/local government, first responders, military - secure connectivity for critical communications
• **Transportation**: Fleet management, asset tracking, connected vehicle solutions
• **Enterprise**: IoT deployments for retail, healthcare, manufacturing, and smart city applications
• **Customer Profile**: Typically enterprise accounts with 100+ devices, high security requirements, and compliance needs

**KAJEET'S COMPETITIVE POSITION:**
• **Primary Competitors**: Verizon Connect, AT&T IoT, T-Mobile Business, Hologram, Particle, Twilio
• **Competitive Advantages**: Carrier-agnostic flexibility, education market expertise, government security clearances, white-glove service
• **Market Challenges**: Larger carriers with direct relationships, price competition, technology complexity

**KAJEET'S TECHNOLOGY STACK:**
• **Connectivity**: Multi-carrier cellular (4G LTE, 5G), private networks, hybrid connectivity
• **Management Platform**: Cloud-based SaaS platform, APIs, mobile applications
• **Security**: Enterprise-grade security, compliance certifications, content filtering
• **Integration**: CRM/ERP integrations, third-party platform APIs, custom development capabilities

**KAJEET'S BUSINESS MODEL:**
• **Revenue Streams**: Monthly connectivity fees, device management platform fees, professional services, hardware sales
• **Pricing Model**: Tiered pricing based on data usage, device count, and service level
• **Customer Lifecycle**: Typically long-term contracts (1-3 years), high customer retention, expansion opportunities

**IDEA TO ANALYZE:**
- **Title:** ${idea.title}
- **Description:** ${idea.description}
- **Category:** ${idea.category}
- **Current Status:** ${idea.status}
- **Internal Effort Score (1-10):** ${idea.effort}
- **Internal Value Score (1-10):** ${idea.value}
- **Priority Score:** ${idea.score?.toFixed(2) || 'N/A'}
- **Tags:** ${idea.tags.join(', ') || 'None'}

**ANALYSIS INSTRUCTIONS:**
Analyze this idea specifically in the context of Kajeet's business model, customer base, competitive position, and technology capabilities. Consider how this idea aligns with Kajeet's core strengths in secure IoT connectivity and device management. Reference Kajeet's specific markets (education, government, transportation, enterprise) and competitive advantages throughout your analysis.
`;

  switch (idea.category) {
    case 'Process':
      return `
You are a senior business process consultant and operations expert specializing in telecommunications and IoT companies. You are analyzing the specific business process improvement idea "${idea.title}" for Kajeet, a leading IoT connectivity provider serving education, government, transportation, and enterprise markets.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Analyze the specific idea "${idea.title}" and how it would improve Kajeet's business operations. Use web search to gather current information about similar process improvements, industry benchmarks, and real-world implementation examples.

## 1. **"${idea.title}" PROCESS ANALYSIS & RESEARCH**
- **Process Being Improved**: Research exactly what process "${idea.title}" aims to improve and search for current industry standards for similar processes in IoT connectivity providers
- **Current Market Data**: Search for current benchmarks and performance data for similar process improvements in IoT and telecommunications companies
- **Customer Impact Research**: Look for case studies and data on how similar process improvements have benefited customers in education, government, transportation, and enterprise IoT sectors
- **Stakeholder Impact Analysis**: Research which teams and functions are typically affected by similar process improvements in IoT companies
- **Cost & ROI Research**: Search for ROI examples from similar process improvement implementations

## 2. **PROCESS IMPROVEMENT RESEARCH**
- **Best Practices**: Search for current best practices in process automation and optimization for telecommunications companies
- **Technology Solutions**: Research available tools, platforms, and automation solutions that could support this process improvement
- **Case Studies**: Look for recent case studies of similar process improvements in IoT or telecommunications companies
- **ROI Data**: Find industry data on typical ROI and time savings from similar process improvements

## 3. **BUSINESS BENEFITS ANALYSIS**
- **Operational Efficiency**: Quantify expected time savings, resource optimization, and error reduction based on industry data
- **Customer Experience**: Research how similar improvements have impacted customer satisfaction in the industry
- **Employee Productivity**: Look for data on staff efficiency improvements and job satisfaction from process automation
- **Cost Savings**: Use current market data to estimate direct and indirect cost reductions
- **Revenue Impact**: Research how process improvements drive revenue growth in similar companies

## 4. **IMPLEMENTATION STRATEGY**
- **Current Market Solutions**: Search for specific vendors, tools, and platforms that could support this implementation
- **Implementation Timelines**: Research typical implementation timelines for similar process improvements
- **Success Metrics**: Find industry-standard KPIs and success metrics for measuring process improvement impact
- **Change Management**: Look for current best practices in change management for process improvements

## 5. **RISK ASSESSMENT & MITIGATION**
- **Industry Challenges**: Research common pitfalls and challenges in similar process improvement projects
- **Mitigation Strategies**: Find proven strategies for managing implementation risks and change resistance
- **Quality Assurance**: Look for current quality control standards and monitoring approaches

## 6. **COMPETITIVE & MARKET ANALYSIS**
- **Competitive Landscape**: Research how competitors are approaching similar process improvements
- **Market Trends**: Find current trends in process automation and operational efficiency in the IoT/telecommunications space
- **Regulatory Considerations**: Search for any regulatory or compliance considerations relevant to this process

Use web search extensively to provide current, data-driven insights rather than generic advice. Include specific numbers, recent case studies, and current market data wherever possible.
`;

    case 'Technology':
      return `
You are a senior technology architect and platform evaluation expert with deep experience in telecommunications, IoT infrastructure, and enterprise technology implementations. You are analyzing a technology platform or infrastructure idea for Kajeet's IoT connectivity and device management business.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Provide a comprehensive technology evaluation focusing on technical feasibility, integration with Kajeet's existing technology stack, and strategic alignment with Kajeet's IoT connectivity and device management platform. Use web search to gather current market data, vendor information, and technology trends specific to IoT connectivity providers.

## 1. **TECHNOLOGY MARKET RESEARCH FOR KAJEET**
- **IoT Technology Landscape**: Search for the current state of this technology category specifically for IoT connectivity providers and device management platforms
- **Kajeet-Relevant Vendors**: Research the top 3-5 vendors/platforms that specifically serve IoT connectivity companies, device management providers, or Kajeet's target markets (education, government, transportation, enterprise)
- **IoT Service Provider Pricing**: Find current pricing structures and total cost of ownership data for IoT connectivity and device management technology solutions
- **Kajeet Competitive Analysis**: Look for recent technology developments by Kajeet's competitors (Verizon Connect, AT&T IoT, T-Mobile Business, Hologram, Particle)

## 2. **VENDOR & COMPETITIVE ANALYSIS**
- **Vendor Comparison**: Research detailed comparisons of leading vendors including features, pricing, and customer reviews
- **Financial Stability**: Look up vendor financial health, recent funding, and market position
- **Customer References**: Search for case studies and customer testimonials from similar companies
- **Integration Ecosystem**: Research available integrations, APIs, and partner ecosystems

## 3. **TECHNICAL FEASIBILITY ASSESSMENT**
- **Architecture Requirements**: Research technical requirements and infrastructure needs for this technology
- **Integration Complexity**: Look for information about integration challenges and best practices
- **Security Standards**: Find current security standards, compliance certifications, and security best practices
- **Performance Benchmarks**: Search for performance data, scalability limits, and reliability metrics

## 4. **INDUSTRY TRENDS & ADOPTION**
- **Adoption Rates**: Research current adoption rates in telecommunications and IoT industries
- **Future Roadmap**: Find information about technology roadmaps and future developments
- **Regulatory Compliance**: Search for relevant regulatory requirements and compliance standards
- **Risk Factors**: Look for known issues, limitations, or challenges with this technology

## 5. **FINANCIAL ANALYSIS**
- **Market Pricing**: Research current market pricing for similar solutions and services
- **ROI Data**: Find case studies and data on typical ROI and payback periods


## 6. **STRATEGIC RECOMMENDATIONS**
- **Implementation Timeline**: Research typical implementation timelines and project phases
- **Best Practices**: Find current best practices for implementing this type of technology
- **Success Factors**: Look for key factors that determine success or failure in similar implementations
- **Alternative Solutions**: Research alternative approaches and competing technologies

Use web search to provide current, specific vendor recommendations, real pricing data, and up-to-date market intelligence rather than generic technology advice.
`;

    case 'Sales and Marketing':
      return `
You are a senior revenue operations and marketing strategy expert with extensive experience in B2B telecommunications, IoT services, and enterprise technology sales. You are analyzing a sales and marketing idea for Kajeet's IoT connectivity and device management business serving education, government, transportation, and enterprise markets.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Provide a comprehensive sales and marketing analysis focusing on lead generation, revenue growth, and sales team effectiveness specific to Kajeet's business model and target markets. Use web search to gather current market data, competitive intelligence, and industry benchmarks for IoT connectivity providers.

## 1. **KAJEET MARKET RESEARCH & COMPETITIVE ANALYSIS**
- **Kajeet Market Size**: Search for current market size data for IoT connectivity and device management services in education, government, transportation, and enterprise sectors
- **Kajeet Segment Growth**: Research current growth rates and market projections specifically for Kajeet's target segments (K-12 education, government agencies, fleet management, enterprise IoT)
- **Kajeet Competitive Landscape**: Look up current competitive positioning of Kajeet vs. major competitors (Verizon Connect, AT&T IoT, T-Mobile Business, Hologram, Particle, Twilio)
- **Kajeet Pricing Analysis**: Find current market pricing for IoT connectivity and device management services and how Kajeet's pricing compares to competitors

## 2. **LEAD GENERATION & CUSTOMER ACQUISITION**
- **Channel Performance**: Research current performance data for different lead generation channels in B2B tech
- **Customer Acquisition Costs**: Find industry benchmarks for CAC in telecommunications and IoT services
- **Conversion Rates**: Look for current conversion rate benchmarks from lead to customer in similar industries
- **Digital Marketing Trends**: Research current trends in B2B digital marketing and lead generation

## 3. **REVENUE IMPACT ANALYSIS**
- **Sales Cycle Data**: Research typical sales cycle lengths and deal sizes in IoT/telecommunications
- **Revenue Growth**: Find case studies of successful revenue growth initiatives in similar companies
- **Customer Lifetime Value**: Look for industry data on customer retention and lifetime value in IoT services
- **Market Penetration**: Research market penetration strategies and success rates

## 4. **SALES & MARKETING EFFECTIVENESS**
- **Sales Productivity**: Find current benchmarks for sales rep productivity and quota attainment
- **Marketing ROI**: Research current marketing ROI benchmarks and measurement best practices
- **Technology Stack**: Look for current best practices in sales and marketing technology for B2B companies
- **Content Marketing**: Research content marketing effectiveness and strategies for technical B2B sales

## 5. **CUSTOMER SEGMENTS & TARGETING**
- **Segment Analysis**: Research current trends and opportunities in education, government, and enterprise IoT
- **Buyer Personas**: Look for recent research on decision-making processes in IoT technology purchases
- **Account-Based Marketing**: Find current ABM strategies and success metrics for B2B technology companies
- **Customer Success**: Research customer success and retention strategies in IoT services

## 6. **IMPLEMENTATION & MEASUREMENT**
- **Marketing Automation**: Research current marketing automation platforms and implementation best practices
- **Sales Enablement**: Look for current sales enablement tools and strategies in technical B2B sales

## 7. **STRATEGIC OPPORTUNITIES**
- **Partnership Channels**: Research current partnership and channel strategies in IoT and telecommunications
- **Emerging Trends**: Look for emerging trends in B2B sales and marketing that could benefit Kajeet
- **Customer Expansion**: Find strategies for expanding within existing customer accounts
- **Market Entry**: Research successful market entry strategies for new segments or geographies

Use web search extensively to provide current market data, specific competitive intelligence, and real-world benchmarks rather than generic sales and marketing advice.
`;

    case 'Feature':
    case 'Product':
    case 'Innovation':
    default:
      return `
You are a senior product strategist and technology advisor with deep expertise in IoT, telecommunications, and enterprise technology solutions. You are analyzing a product idea for Kajeet's IoT connectivity and device management platform serving education, government, transportation, and enterprise markets.

${commonContext}

**ANALYSIS REQUIREMENTS:**
Provide a strategic analysis using current market data, competitive intelligence, and real-time industry insights specific to Kajeet's business model and target markets. Use web search to gather up-to-date information about market trends, competitor moves, and customer needs in IoT connectivity and device management.

## 1. **KAJEET MARKET RESEARCH & COMPETITIVE LANDSCAPE**
- **Kajeet Market Opportunity**: Search for current market size data and growth projections for this product category specifically in education, government, transportation, and enterprise IoT markets
- **Kajeet Competitive Analysis**: Research current competitive landscape including recent product launches by Kajeet's competitors (Verizon Connect, AT&T IoT, T-Mobile Business, Hologram, Particle)
- **Kajeet Customer Demand**: Look for recent surveys, reports, or analysis about customer needs and pain points in IoT connectivity and device management for Kajeet's target sectors
- **IoT Technology Trends**: Find current technology trends and emerging developments in IoT connectivity and device management that support or challenge this idea

## 2. **KAJEET CUSTOMER SEGMENT ANALYSIS**
- **Kajeet Target Markets**: Research current trends and opportunities specifically in Kajeet's core markets (K-12 education, government agencies, transportation/fleet management, enterprise IoT) and how this product idea serves those segments
- **Kajeet Buyer Behavior**: Look for recent research on how Kajeet's target customers (IT directors, fleet managers, government procurement) evaluate and purchase IoT connectivity and device management solutions
- **Kajeet Customer Pain Points**: Find current customer feedback and pain points specific to IoT connectivity and device management in education, government, transportation, and enterprise sectors
- **Kajeet Value Perception**: Research pricing sensitivity and value perception for IoT connectivity services and device management platforms in Kajeet's target markets

## 3. **TECHNICAL LANDSCAPE ASSESSMENT**
- **Technology Stack**: Research current technology standards and best practices for this type of solution
- **Integration Requirements**: Look for information about integration complexity and requirements
- **Security Standards**: Find current security standards and compliance requirements
- **Scalability Considerations**: Research scalability challenges and solutions in similar implementations

## 4. **BUSINESS MODEL & FINANCIAL ANALYSIS**
- **Revenue Models**: Research successful revenue models for similar products in the IoT space
- **Pricing Strategies**: Look for current pricing strategies and market rates for comparable solutions
- **Investment Requirements**: Find typical development and go-to-market investment requirements
- **ROI Expectations**: Research typical ROI expectations and payback periods for similar product investments

## 5. **GO-TO-MARKET STRATEGY**
- **Launch Strategies**: Research successful product launch strategies in IoT and telecommunications
- **Channel Partnerships**: Look for successful partnership strategies and channel programs
- **Customer Acquisition**: Find effective customer acquisition strategies for technical B2B products
- **Marketing Approaches**: Research current marketing approaches and messaging for similar products

## 6. **RISK ASSESSMENT & MITIGATION**
- **Market Risks**: Research current market challenges and potential disruptions
- **Technology Risks**: Look for common technical challenges and failure points in similar products
- **Competitive Threats**: Find information about potential competitive responses and market entry
- **Regulatory Considerations**: Research any regulatory or compliance requirements

## 7. **STRATEGIC RECOMMENDATIONS**
- **Market Timing**: Research market readiness and optimal timing for product launch
- **Success Factors**: Look for case studies of successful and failed similar product launches
- **Partnership Opportunities**: Find potential strategic partnerships that could accelerate success
- **Long-term Vision**: Research how this product category is expected to evolve over the next 3-5 years

Use web search extensively to provide current, specific market data and competitive intelligence rather than generic product strategy advice. Include recent case studies, current pricing data, and real customer feedback wherever possible.
`;
  }
};

// Standard AI Analysis without web search (faster)
export const analyzeIdeaStandard = async (idea: Idea): Promise<string> => {
  try {
    const prompt = getStandardAnalysisPrompt(idea);

    const response = await fetch(AI_ANALYSIS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        analysisType: 'standard'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }

    const data = await response.json();
    
    if (data.analysis && data.analysis.trim()) {
      // Filter out procedural messaging and return clean analysis
      return filterAnalysisContent(data.analysis);
    } else {
      throw new Error('No analysis content received from API');
    }

  } catch (error: any) {
    console.error('Error calling AI Analysis API (Standard):', error);
    throw new Error('Standard AI analysis failed. Please try again later.');
  }
};

// Deep Research Analysis with web search (slower but comprehensive)
export const analyzeIdeaDeepResearch = async (idea: Idea): Promise<string> => {
  try {
    const prompt = getCategorySpecificPrompt(idea);

    const response = await fetch(AI_ANALYSIS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        analysisType: 'deep'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }

    const data = await response.json();
    
    if (data.analysis && data.analysis.trim()) {
      // Filter out procedural messaging and return clean analysis
      return filterAnalysisContent(data.analysis);
    } else {
      throw new Error('No analysis content received from API');
    }

  } catch (error: any) {
    console.error('Error calling AI Analysis API (Deep Research):', error);
    throw new Error('Deep research analysis failed. Please try again later.');
  }
};

// Legacy function that now calls the deep research version for backward compatibility
export const analyzeIdea = async (idea: Idea): Promise<string> => {
  return analyzeIdeaDeepResearch(idea);
};

// Fallback function with mock analysis for development
export const getMockAnalysis = (idea: Idea): string => {
  switch (idea.category) {
    case 'Process':
      return `**AI Analysis for: ${idea.title}** (Process Improvement)

**🔄 Kajeet Process Assessment**
• Identified process: ${idea.title.toLowerCase().includes('customer') ? 'Customer service/support workflow for IoT connectivity customers' : 'Internal operational process for device management and connectivity services'}
• Pain points: ${idea.effort > 6 ? 'Multiple bottlenecks affecting Kajeet customer experience and operational efficiency' : 'Some inefficiencies in current IoT service delivery workflow'}
• Stakeholder impact: ${idea.value > 7 ? 'Multiple Kajeet departments (operations, customer success, engineering) and customer segments (education, government, transportation, enterprise)' : 'Primarily internal Kajeet teams supporting IoT connectivity services'}
• Current costs: Estimated ${idea.effort * 10}-${idea.effort * 15}% of operational time spent on manual processes related to IoT device management and connectivity

**⚡ Process Improvement Benefits**
• Efficiency gains: Expected ${20 + (idea.value * 5)}%-${30 + (idea.value * 8)}% time savings
• Customer impact: ${idea.value > 6 ? 'Significant improvement in customer satisfaction and response times' : 'Moderate customer experience enhancement'}
• Cost reduction: Potential ${idea.value * 15}K-${idea.value * 25}K annual savings in operational costs
• Employee productivity: ${idea.value > 7 ? 'Major reduction in repetitive tasks, improved job satisfaction' : 'Moderate productivity improvements'}

**📊 Implementation Strategy**
• Rollout approach: ${idea.effort > 6 ? 'Phased implementation - pilot with one team first' : 'Direct rollout across affected departments'}
• Training requirements: ${idea.effort > 7 ? 'Comprehensive change management and training program' : 'Standard process training and documentation'}
• Success metrics: Process cycle time, error rates, customer satisfaction scores
• Timeline: ${idea.effort > 6 ? '3-6 months including pilot phase' : '2-4 months full implementation'}

**💼 ROI Analysis**
• Cost-benefit ratio: ${(idea.value / idea.effort).toFixed(1)}:1 return on investment
• Payback period: ${Math.ceil(idea.effort / 2)} months
• Long-term value: Process standardization, scalability improvements, knowledge retention

**⚠️ Implementation Risks**
• Change resistance: ${idea.effort > 7 ? 'High - requires strong change management' : 'Moderate - standard adoption challenges'}
• Business continuity: ${idea.effort > 8 ? 'Careful transition planning required' : 'Minimal disruption expected'}
• Quality assurance: New quality checkpoints and monitoring systems needed

*Note: This is a mock analysis for development. Configure your Anthropic API key to enable real AI analysis.*`;

    case 'Technology':
      return `**AI Analysis for: ${idea.title}** (Technology Platform)

**🛠️ Kajeet Technology Assessment**
• Platform type: ${idea.title.toLowerCase().includes('cloud') ? 'Cloud infrastructure platform for IoT connectivity management' : idea.title.toLowerCase().includes('api') ? 'API/Integration platform for device management and connectivity services' : 'Enterprise software platform for IoT service delivery'}
• Technical complexity: ${idea.effort > 7 ? 'High - significant integration with Kajeet\'s existing IoT connectivity platform and carrier networks' : 'Medium - standard configuration and setup with current device management infrastructure'}
• Architecture alignment: ${idea.value > 6 ? 'Strong fit with existing Kajeet technology stack (multi-carrier connectivity, device management platform, security infrastructure)' : 'Requires some architectural adjustments to integrate with Kajeet\'s IoT connectivity and device management systems'}
• Integration scope: ${idea.effort > 6 ? 'Multiple system integrations required with carrier networks, device management platform, and customer portals' : 'Limited integration complexity with existing Kajeet IoT infrastructure'}

**💻 Technical Implementation**
• Infrastructure needs: ${idea.effort > 7 ? 'Significant cloud resources and infrastructure scaling' : 'Standard infrastructure requirements'}
• Development effort: ${idea.effort * 20}-${idea.effort * 35} development hours estimated
• Security considerations: ${idea.value > 7 ? 'Enterprise-grade security and compliance requirements' : 'Standard security protocols sufficient'}
• Performance impact: Expected ${idea.value > 6 ? 'significant' : 'moderate'} improvement in system performance

**🏢 Vendor & Platform Analysis**
• Market leaders: ${idea.effort > 6 ? '5+ established vendors with strong market presence' : '3-4 reliable vendor options available'}
• Vendor stability: ${idea.value > 7 ? 'Tier 1 vendors with strong financial backing' : 'Mix of established and emerging vendors'}
• Total cost of ownership: $${idea.effort * 15}K-$${idea.effort * 30}K annually including licensing and support
• ROI timeline: ${Math.ceil(idea.effort / 1.5)}-${Math.ceil(idea.effort / 1)} months payback period

**📈 Business Impact**
• Operational efficiency: ${10 + (idea.value * 5)}%-${20 + (idea.value * 8)}% improvement in operational workflows
• Customer experience: ${idea.value > 6 ? 'Direct positive impact on customer service delivery' : 'Indirect customer benefits through improved operations'}
• Competitive advantage: ${idea.value > 7 ? 'Significant differentiation opportunity' : 'Moderate competitive positioning improvement'}

**🎯 Strategic Recommendations**
• Recommendation: ${idea.value / idea.effort > 1.5 ? 'PROCEED - Strong ROI and strategic alignment' : idea.value / idea.effort > 1 ? 'EVALUATE - Conduct detailed POC' : 'DEFER - Consider alternatives'}
• Implementation approach: ${idea.effort > 7 ? 'Phased rollout with comprehensive testing' : 'Standard implementation timeline'}
• Vendor selection: Prioritize ${idea.value > 7 ? 'enterprise-grade solutions with proven track records' : 'cost-effective solutions with good support'}

*Note: This is a mock analysis for development. Configure your Anthropic API key to enable real AI analysis.*`;

    case 'Sales and Marketing':
      return `**AI Analysis for: ${idea.title}** (Sales & Marketing Initiative)

**🎯 Kajeet Market Opportunity & Lead Generation**
• Target segments: ${idea.title.toLowerCase().includes('enterprise') ? 'Enterprise IoT deployments and government agencies' : idea.title.toLowerCase().includes('education') ? 'K-12 education and digital equity programs' : 'Multi-vertical opportunity across education, government, transportation, and enterprise IoT'}
• Lead generation potential: ${idea.value * 50}-${idea.value * 100} additional qualified leads monthly in Kajeet's target markets
• Market penetration: ${idea.value > 6 ? 'High potential for existing account expansion in education, government, and enterprise IoT sectors' : 'Moderate upsell opportunities within current Kajeet customer base'}
• Competitive positioning: ${idea.value > 7 ? 'Strong differentiation vs. competitors (Verizon Connect, AT&T IoT, T-Mobile Business)' : 'Moderate competitive advantage in IoT connectivity and device management'}

**💰 Revenue Impact Analysis**
• Pipeline acceleration: Expected ${15 + (idea.value * 5)}% reduction in sales cycle length
• Deal size impact: Potential ${10 + (idea.value * 8)}%-${20 + (idea.value * 12)}% increase in average contract value
• Customer acquisition: ${idea.value > 6 ? 'Significant CAC reduction through improved targeting' : 'Moderate CAC improvements'}
• Revenue expansion: $${idea.value * 100}K-$${idea.value * 200}K additional ARR potential

**🚀 Sales Team Effectiveness**
• Productivity improvement: ${idea.value * 8}%-${idea.value * 15}% increase in sales rep efficiency
• Lead qualification: ${idea.effort > 6 ? 'Advanced scoring and qualification capabilities' : 'Improved but standard qualification process'}
• Sales enablement: ${idea.value > 7 ? 'Comprehensive content and tool enhancements' : 'Targeted sales support improvements'}
• Performance metrics: Conversion rates, pipeline velocity, quota attainment

**📊 Marketing ROI & Effectiveness**
• Campaign performance: Expected ${20 + (idea.value * 10)}%-${30 + (idea.value * 15)}% improvement in marketing ROI
• Digital marketing: ${idea.value > 6 ? 'Multi-channel optimization with advanced attribution' : 'Focused channel improvements'}
• Content strategy: ${idea.effort > 6 ? 'Comprehensive content hub development' : 'Targeted content creation'}
• Brand awareness: ${idea.value > 7 ? 'Significant market presence enhancement' : 'Moderate brand positioning improvement'}

**👥 Implementation Requirements**
• Team needs: ${idea.effort > 7 ? 'Additional FTE for sales ops and marketing automation' : 'Standard resource allocation'}
• Technology stack: CRM, marketing automation, ${idea.effort > 6 ? 'advanced analytics platforms' : 'standard tracking tools'}
• Budget allocation: $${idea.effort * 25}K-$${idea.effort * 50}K for tools and campaigns
• Timeline: ${idea.effort > 6 ? '4-6 months full implementation' : '2-4 months to full deployment'}

**📈 Success Measurement**
• Revenue attribution: ${idea.value > 6 ? 'Advanced multi-touch attribution model' : 'Standard last-touch attribution'}
• KPIs: MQL-to-SQL conversion, sales velocity, customer acquisition cost, lifetime value
• ROI tracking: Expected ${Math.round(idea.value / idea.effort * 100)}%-${Math.round(idea.value / idea.effort * 150)}% ROI within 12 months

*Note: This is a mock analysis for development. Configure your Anthropic API key to enable real AI analysis.*`;

    case 'Feature':
    case 'Product':
    case 'Innovation':
    default:
      return `**AI Analysis for: ${idea.title}** (Product/Feature)

**🎯 Kajeet Market Opportunity and Competition**
• Market size: ${idea.value > 7 ? 'Large addressable market in IoT connectivity and device management with strong growth potential in education, government, and enterprise sectors' : 'Moderate market opportunity with steady growth in Kajeet\'s target IoT segments'}
• Competition level: ${idea.effort > 7 ? 'High - established players (Verizon Connect, AT&T IoT, T-Mobile Business) with significant market share in IoT connectivity' : 'Medium - some competitive pressure but opportunities exist for Kajeet\'s carrier-agnostic approach'}
• Differentiation potential: ${idea.value > 7 ? 'Strong unique value proposition aligned with Kajeet\'s strengths in secure IoT connectivity and device management' : 'Moderate differentiation opportunity leveraging Kajeet\'s education and government market expertise'}
• Customer segments: ${idea.value > 6 ? 'Multiple high-value segments including K-12 education, government agencies, transportation/fleet management, and enterprise IoT' : 'Focused customer segment with clear need for IoT connectivity and device management'}

**⚙️ Technical Implementation**
• Development complexity: ${idea.effort > 6 ? 'High - requires significant engineering resources and architecture changes' : 'Medium - manageable with current technical capabilities'}
• Technology stack: ${idea.effort > 7 ? 'Multiple technology integrations and potential platform changes' : 'Standard development with existing tools and platforms'}
• Scalability: ${idea.value > 6 ? 'High scalability potential across customer base' : 'Moderate scalability with targeted deployment'}
• Timeline estimate: ${idea.effort > 7 ? '6-12 months including testing and rollout' : '3-6 months to market'}

**💰 Business Value & ROI**
• Revenue model: ${idea.title.toLowerCase().includes('subscription') || idea.value > 7 ? 'Recurring subscription revenue with strong margins' : 'Mixed revenue model with good profit potential'}
• Expected ROI: ${idea.value > 7 ? 'High - significant revenue and competitive advantage' : 'Medium - solid business case with good returns'}
• Customer value: ${idea.value * 15}%-${idea.value * 25}% improvement in customer operational efficiency
• Pricing strategy: ${idea.value > 6 ? 'Premium positioning with value-based pricing' : 'Competitive pricing with market penetration focus'}

**📋 Strategic Recommendations**
1. ${idea.effort > 7 ? 'Conduct detailed technical architecture review and resource planning' : 'Proceed with standard product development process'}
2. ${idea.value > 6 ? 'Develop comprehensive go-to-market strategy with pilot customers' : 'Create focused launch plan for target segments'}
3. ${idea.value > 7 ? 'Consider strategic partnerships to accelerate market entry' : 'Leverage existing customer relationships for validation'}
4. ${idea.effort > 6 ? 'Plan phased rollout to manage complexity and risk' : 'Target direct market launch with existing capabilities'}

**⚠️ Risk Assessment**
• Technical risks: ${idea.effort > 8 ? 'High complexity may impact timeline and resources' : 'Standard development risks manageable with good project management'}
• Market risks: ${idea.value < 5 ? 'Limited market validation - consider customer research' : 'Good market alignment with manageable competitive pressure'}
• Business risks: ${idea.value / idea.effort < 1 ? 'ROI may not justify investment - consider scope reduction' : 'Positive business case with acceptable risk profile'}

*Note: This is a mock analysis for development. Configure your Anthropic API key to enable real AI analysis.*`;
  }
};

export const formatAnalysisText = (text: string): string => {
  if (!text) return '';
  
  let formatted = text;
  
  // Convert headers
  formatted = formatted.replace(/^### (.+)$/gm, '<h4 class="text-base font-semibold text-gray-800 mt-4 mb-2">$1</h4>');
  formatted = formatted.replace(/^## (.+)$/gm, '<h3 class="text-lg font-bold text-gray-900 mt-6 mb-3">$1</h3>');
  formatted = formatted.replace(/^# (.+)$/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-4">$1</h2>');
  
  // Convert bold text
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
  
  // Convert bullet points (handle nested bullets)
  const lines = formatted.split('\n');
  const processedLines = [];
  let inList = false;
  let listDepth = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if it's a bullet point
    const bulletMatch = line.match(/^(\s*)[-*+]\s(.+)$/);
    if (bulletMatch) {
      const indent = bulletMatch[1].length;
      const content = bulletMatch[2];
      
      if (!inList) {
        processedLines.push('<ul class="list-disc ml-4 mb-4 space-y-2">');
        inList = true;
        listDepth = indent;
      } else if (indent > listDepth) {
        processedLines.push('<ul class="list-disc ml-2 mt-2 space-y-1">');
        listDepth = indent;
      } else if (indent < listDepth) {
        processedLines.push('</ul>');
        listDepth = indent;
      }
      
      processedLines.push(`<li class="text-gray-700 leading-relaxed">${content}</li>`);
      continue;
    }
    
    // Check if it's a numbered list
    const numberedMatch = line.match(/^(\s*)(\d+\.)\s(.+)$/);
    if (numberedMatch) {
      const indent = numberedMatch[1].length;
      const content = numberedMatch[3];
      
      if (!inList) {
        processedLines.push('<ol class="list-decimal ml-4 mb-4 space-y-2">');
        inList = true;
        listDepth = indent;
      } else if (indent > listDepth) {
        processedLines.push('<ol class="list-decimal ml-2 mt-2 space-y-1">');
        listDepth = indent;
      } else if (indent < listDepth) {
        processedLines.push('</ol>');
        listDepth = indent;
      }
      
      processedLines.push(`<li class="text-gray-700 leading-relaxed">${content}</li>`);
      continue;
    }
    
    // If we were in a list and this isn't a list item, close the list
    if (inList && line.trim() !== '') {
      processedLines.push('</ul></ol>'.slice(0, -5)); // Close any open list
      inList = false;
      listDepth = 0;
    }
    
    // Regular line
    if (line.trim() === '') {
      processedLines.push('<br>');
    } else if (!line.match(/^<h[2-4]/)) {
      // Only wrap in paragraph if it's not already a header
      processedLines.push(`<p class="text-gray-700 leading-relaxed mb-3">${line}</p>`);
    } else {
      processedLines.push(line);
    }
  }
  
  // Close any remaining open lists
  if (inList) {
    processedLines.push('</ul></ol>'.slice(0, -5));
  }
  
  formatted = processedLines.join('\n');
  
  // Clean up extra breaks and spacing
  formatted = formatted.replace(/<br>\s*<br>/g, '<br>');
  formatted = formatted.replace(/<\/p>\s*<br>/g, '</p>');
  formatted = formatted.replace(/<br>\s*<p/g, '<p');
  
  return formatted;
}; 